import { cn } from "@/utils";
import { FileDown, ListFilter, X } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "./Button";

/**
 * Cabeçalho do `DataList`: filtrar, ver o que está filtrado, exportar.
 *
 * O visor de filtros aplicados existe porque a causa número um de "o sistema
 * está errado, sumiu cobrança" é um filtro esquecido de ontem. Enquanto o
 * critério fica escondido dentro do modal, o usuário olha uma lista curta e
 * conclui que faltou dado. Aqui o que está filtrado fica na cara, e cada item
 * sai com um clique.
 */

export type AppliedFilter = {
  id: string;
  /** A dimensão: "Status", "Período", "Loja". */
  label: string;
  /** O valor escolhido: "Em atraso", "Últimos 30 dias". */
  value: string;
  /** Sem isto, a etiqueta é só leitura (filtro travado pela tela). */
  onRemove?: () => void;
};

export interface DataListHeaderProps {
  title?: string;
  /**
   * `2` é o padrão. Use `1` quando a lista É a tela — aí o título dela é o
   * título da página, e a página não pode começar num h2.
   */
  titleLevel?: 1 | 2;
  /** Quantos registros esta página mostra. Some enquanto `isLoading`. */
  count?: number;
  /**
   * Total de registros do resultado inteiro, não só desta página. Com ele a
   * contagem vira "10 / 5.930": sem o total, o usuário lê "10" e conclui que o
   * filtro devolveu dez registros, quando são dez de milhares.
   */
  total?: number;
  /** Complemento do título: "atualizado há 2 minutos". */
  description?: ReactNode;
  /**
   * Busca por texto (`SearchField`). Fica antes de "Filtros": é o caminho mais
   * curto para quem já sabe o que procura, e fechada custa os mesmos 44px de um
   * botão de ícone.
   */
  search?: ReactNode;
  onFilter?: () => void;
  appliedFilters?: AppliedFilter[];
  /** Só aparece com dois ou mais filtros — com um, a etiqueta já resolve. */
  onClearFilters?: () => void;
  onExport?: () => void;
  isExporting?: boolean;
  exportLabel?: string;
  /**
   * Conteúdo à esquerda quando a lista não tem título — tipicamente as abas de
   * visão (`Tabs variant="pill"`). Assim busca e filtros sobem para a mesma
   * linha das abas, em vez de gastar uma linha só para eles acima da tabela.
   */
  leading?: ReactNode;
  /** Ação principal da tela, como "Nova cobrança". */
  action?: ReactNode;
  isLoading?: boolean;
  className?: string;
}

/** O título sai como h1 ou h2 conforme o papel da lista na tela. */
const TituloDaLista = ({
  level,
  className,
  children,
}: {
  level: 1 | 2;
  className?: string;
  children: ReactNode;
}) => {
  const Tag = level === 1 ? "h1" : "h2";
  return <Tag className={className}>{children}</Tag>;
};

export const DataListHeader = ({
  title,
  titleLevel = 2,
  count,
  total,
  description,
  search,
  onFilter,
  appliedFilters = [],
  onClearFilters,
  onExport,
  isExporting = false,
  exportLabel = "Gerar planilha",
  leading,
  action,
  isLoading = false,
  className,
}: DataListHeaderProps) => {
  const quantidade = appliedFilters.length;
  const temFiltros = quantidade > 0;

  return (
    // `relative`: a região sr-only que anuncia a contagem de filtros é
    // `position: absolute` e, solta, se ancoraria no documento.
    <div className={cn("relative flex flex-col gap-3", className)}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {title ? (
          <div className="flex flex-col gap-0.5">
            <TituloDaLista
              level={titleLevel}
              className={cn(
                "text-ds-ink-900",
                // Título de página vai na serifada da marca; título de lista
                // dentro de uma tela já titulada fica em Sora, que é o que o
                // produto faz com título de card e de seção.
                titleLevel === 1
                  ? "font-ds-display text-ds-h3 font-bold"
                  : "font-ds-sans text-ds-h4 font-semibold"
              )}
            >
              {title}
              {count !== undefined && !isLoading ? (
                <>
                  <span
                    aria-hidden="true"
                    className="ds-numeric text-ds-ink-600 ml-2 font-normal"
                  >
                    {count.toLocaleString("pt-BR")}
                    {total !== undefined
                      ? ` / ${total.toLocaleString("pt-BR")}`
                      : null}
                  </span>
                  {/* "10 / 5.930" é claro para quem vê a tabela e enigmático
                      para quem escuta a página. */}
                  <span className="sr-only">
                    {total !== undefined
                      ? `${count} de ${total} registros`
                      : `${count} registros`}
                  </span>
                </>
              ) : null}
            </TituloDaLista>
            {description ? (
              <p className="font-ds-sans text-ds-small text-ds-ink-600">
                {description}
              </p>
            ) : null}
          </div>
        ) : (
          (leading ?? <span />)
        )}

        {/* Em mobile os botões ocupam a linha inteira, lado a lado: são as duas
            ações da tela e não competem com o conteúdo. */}
        <div className="flex flex-wrap items-center gap-2">
          {search}

          {onFilter ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={onFilter}
              disabled={isLoading}
              iconLeft={<ListFilter />}
              aria-label={
                temFiltros
                  ? `Filtros, ${quantidade} ${quantidade === 1 ? "aplicado" : "aplicados"}`
                  : "Filtros"
              }
            >
              Filtros
              {temFiltros ? (
                <span
                  aria-hidden="true"
                  className="ds-numeric bg-ds-brand-600 text-ds-caption text-ds-on-solid ml-1 inline-flex min-w-5 items-center justify-center rounded-full px-1.5 py-0.5 font-semibold"
                >
                  {quantidade}
                </span>
              ) : null}
            </Button>
          ) : null}

          {onExport ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={onExport}
              isLoading={isExporting}
              loadingLabel="Gerando planilha"
              disabled={isLoading}
              iconLeft={<FileDown />}
            >
              {isExporting ? "Gerando…" : exportLabel}
            </Button>
          ) : null}

          {action}
        </div>
      </div>

      {temFiltros ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-ds-sans text-ds-small text-ds-ink-600">
            Filtrando por:
          </span>

          {/* Cápsula de 44px e texto de 14px: o valor do filtro é a informação
              que evita o "sumiu cobrança", e `text-ds-caption` (12px) é
              reservado a metadado. O "x" também tem 44px — a régua do alvo de
              toque vale para ele como para qualquer botão. */}
          {appliedFilters.map(filtro => (
            <span
              key={filtro.id}
              className="border-ds-brand-200 bg-ds-brand-50 font-ds-sans text-ds-small text-ds-brand-700 inline-flex min-h-11 items-center gap-1 rounded-full border py-1 pr-1 pl-4"
            >
              <span>
                <span className="font-normal">{filtro.label}: </span>
                <span className="font-semibold">{filtro.value}</span>
              </span>
              {filtro.onRemove ? (
                <button
                  type="button"
                  onClick={filtro.onRemove}
                  aria-label={`Remover filtro ${filtro.label}: ${filtro.value}`}
                  className="hover:bg-ds-brand-200 focus-visible:ring-ds-focus focus-visible:ring-offset-ds-brand-50 inline-flex size-11 cursor-pointer items-center justify-center rounded-full transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none"
                >
                  <X aria-hidden="true" className="size-4" />
                </button>
              ) : (
                <span className="w-2" />
              )}
            </span>
          ))}

          {/* Com um filtro só, o "x" da própria etiqueta já resolve. */}
          {onClearFilters && quantidade > 1 ? (
            <button
              type="button"
              onClick={onClearFilters}
              className="rounded-ds-sm font-ds-sans text-ds-small text-ds-ink-700 hover:text-ds-ink-900 focus-visible:ring-ds-focus focus-visible:ring-offset-ds-paper inline-flex min-h-11 cursor-pointer items-center px-3 font-medium underline underline-offset-2 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
            >
              Limpar tudo
            </button>
          ) : null}

          {/* Muda a lista sem mexer no foco de quem navega por teclado. */}
          <span role="status" aria-live="polite" className="sr-only">
            {quantidade}{" "}
            {quantidade === 1 ? "filtro aplicado" : "filtros aplicados"}.
          </span>
        </div>
      ) : null}
    </div>
  );
};
